import React from 'react'

import { FiftyFiftyProps } from 'lib/components/FiftyFifty/FiftyFifty'
import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'

export interface WhatCanIShipPageContent {
  hero: NarrativeHeroProps
  fiftyFiftiesMeta: FiftyFiftyProps[]
}

export interface NarrativeStep {
  body: string | React.ReactNode
}

const BACKGROUND_IMAGE = '/images/shipsticks/what-can-i-ship-hero.jpg'

const hero: NarrativeHeroProps = {
  header: 'What you can ship with Ship&Play',
  subHeader: 'Suitcases, sporting gear, family essentials, and just about everything else.',
  backgroundImage: BACKGROUND_IMAGE,
}

const fiftyFiftiesMeta: FiftyFiftyProps[] = [
  {
    backgroundColor: 'grey',
    body: `
            Suitcases, duffels, roller bags - whatever you'd normally check, send it ahead
            instead. Pack as you always do, attach your label, and travel completely bag-free.
    `,
    showEyebrow: false,
    header: 'Ship luggage',
    image: {
      src: '/images/shipsticks/what-can-i-ship-1.jpg',
    },
    layout: 'image-right',
    button: {
      text: 'How to pack',
      link: {
        href: '/pack',
      },
    },
  },
  {
    backgroundColor: 'grey',
    body: `
            Golf clubs, skis, snowboards, bikes - the gear that makes a trip worth taking is
            also the worst to lug through an airport. Pack it in a soft bag, hard case, or box,
            and we'll have it waiting wherever the fun is.
    `,
    showEyebrow: false,
    header: 'Ship sporting gear',
    image: {
      src: '/images/shipsticks/what-can-i-ship-2.jpg',
    },
    layout: 'image-left',
  },
  {
    backgroundColor: 'grey',
    body: `
            Planning a family reunion, corporate retreat, group tour, or tournament? Send
            everyone's bags and gear to a single destination - and back home again - without
            anyone wrestling a bag through the terminal.
    `,
    showEyebrow: false,
    header: 'Group & event shipments',
    image: {
      src: '/images/shipsticks/what-can-i-ship-3.jpg',
    },
    layout: 'image-right',
    button: {
      text: 'Learn more',
      link: {
        href: 'https://www.shipplay.com/form/group-shipping-request',
      },
    },
  },
  {
    backgroundColor: 'grey',
    body: `
            Traveling with more than the basics - a stroller, car seat, push cart, or some
            oversized prized possession? No problem. Our customer support team is happy to
            put together a custom quote. Contact us to get started.
    `,
    showEyebrow: false,
    header: 'Ship specialty & family items',
    image: {
      src: '/images/shipsticks/what-can-i-ship-4.jpg',
    },
    layout: 'image-left',
    button: {
      text: 'Contact us',
      link: {
        href: '/contact',
      },
    },
  },
]

const content: WhatCanIShipPageContent = {
  hero,
  fiftyFiftiesMeta,
}

/**
 * Copy for the Ship&Play port (see ShipplayWhatCanIShipPage). Kept here so the
 * page component owns only the render tree. Bodies are concatenated across
 * lines to stay under max-len.
 */
export interface SizeCardData {
  title: string
  weight: string
  imgUrl: string
  imgAlt: string
  linear: string
  dims: string
}

interface WcisSplitContent {
  body: string
  imgUrl: string
  imgAlt: string
  // Optional small graphic shown above the heading in the copy column
  // (playground only uses this on the Luggage split).
  companionImgUrl?: string
  companionImgAlt?: string
}

interface WcisSplitContentWithCta extends WcisSplitContent {
  cta: { text: string; to: string }
}

export interface ShipplayWhatCanIShipContent {
  hero: { imgUrl: string; subtitle: string }
  luggage: WcisSplitContentWithCta
  luggageSizes: SizeCardData[]
  boxes: WcisSplitContentWithCta
  boxSizes: SizeCardData[]
  family: WcisSplitContent
  sports: WcisSplitContentWithCta
  cantShip: string[]
}

export const shipplayContent: ShipplayWhatCanIShipContent = {
  hero: {
    imgUrl: '/images/shipplay/hero/what-can-i-ship.webp',
    subtitle: 'Ship luggage, family essentials, sports equipment, and more.',
  },
  luggage: {
    body:
      'We handle all your luggage, big or small. Rollers, duffels, carry-ons, oversize ' +
      'suitcases, and even specialty bags—we ship it all.',
    imgUrl: '/images/shipplay/wcis-luggage.webp',
    imgAlt: 'Luggage category of items Ship&Play ships',
    companionImgUrl: '/images/shipplay/companions/LuggageInventory.webp',
    companionImgAlt: 'Ship luggage',
    cta: { text: 'How to Pack Your Luggage', to: '/pack' },
  },
  luggageSizes: [
    {
      title: 'Carry On',
      weight: 'Up to 25lbs',
      imgUrl: '/images/shipplay/inventory/luggage_sm.webp',
      imgAlt: 'Carry On: up to 25lbs',
      linear: '45 in. ( L + W + H )',
      dims: '25 + 15 + 5',
    },
    {
      title: 'Checked',
      weight: 'Up to 50lbs',
      imgUrl: '/images/shipplay/inventory/luggage_md.webp',
      imgAlt: 'Checked bags: up to 50lbs',
      linear: '62 in. ( L + W + H )',
      dims: '26 + 16 + 20',
    },
    {
      title: 'Oversized',
      weight: 'Up to 65lbs',
      imgUrl: '/images/shipplay/inventory/luggage_lg.webp',
      imgAlt: 'Oversized bags: up to 65lbs',
      linear: '74 in. ( L + W + H )',
      dims: '41 + 11 + 22',
    },
  ],
  boxes: {
    body:
      'Whether you’re moving, shipping a few personal belongings, headed off to college, or ' +
      'sending supplies for an extended stay, we ship your boxes safely and on time.',
    imgUrl: '/images/shipplay/hero/pack-boxes.webp',
    imgAlt: 'Boxes and general packages that Ship&Play ships',
    cta: { text: 'How to Pack Your Boxes', to: '/pack/boxes' },
  },
  boxSizes: [
    {
      title: 'Standard',
      weight: 'Up to 42lbs',
      imgUrl: '/images/shipplay/inventory/box-small.webp',
      imgAlt: 'Standard box: up to 42lbs',
      linear: '72 in. ( L + W + H )',
      dims: '48 + 12 + 12',
    },
    {
      title: 'Extra Large',
      weight: 'Up to 56lbs',
      imgUrl: '/images/shipplay/inventory/box-large.webp',
      imgAlt: 'Extra large box: up to 56lbs',
      linear: '79 in. ( L + W + H )',
      dims: '50 + 16 + 13',
    },
  ],
  family: {
    body:
      'From strollers and car seats to walkers and mobility aids, we make it easy to keep the ' +
      'whole family moving. Just box them up, and free your hands for wrangling little ones, ' +
      'guiding grandparents, or, for once, just not spilling your coffee.',
    imgUrl: '/images/shipplay/wcis-family.webp',
    imgAlt: 'Family stroller, representing personal and family items Ship&Play ships',
  },
  sports: {
    body:
      'Hit the slopes, the fairway, the trail, and beyond without worrying about your equipment ' +
      'getting damaged by the airlines or settling for rentals. Ship everything from skis and ' +
      'snowboards to golf clubs, bikes, and more.',
    imgUrl: '/images/shipplay/wcis-sports.webp',
    imgAlt: 'Sports equipment, such as skis and golf clubs, that Ship&Play ships.',
    cta: { text: 'How to Pack', to: '/pack' },
  },
  cantShip: [
    'Alcohol',
    'Drugs',
    'Jewelry',
    'Electronics',
    'Hazardous materials',
    'Pressurized containers',
    'Firearms / ammunition',
  ],
}

export default content
